import React, {memo, ReactNode, useCallback, useEffect, useRef} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import {Text} from '../Text'
import {TabsVariant, useTabsContext} from './Tabs'

const FOCUS_RING_OFFSET = 2
const HOVER_TRANSITION_DURATION = 200

interface TabProps {
  /** Unique value for this tab */
  value: string
  /** Tab label text */
  label: string
  /** Whether the tab is disabled */
  disabled?: boolean
  /** Additional CSS class */
  className?: string
  /** Data ID for testing */
  dataId?: string
  /** Icon element to display before label */
  icon?: ReactNode
  /** Variant passed from parent Tabs component */
  variant?: TabsVariant
}

const TabButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${cssTheme.layout.spacing.s200};
  background: none;
  border: none;
  outline: none;
  position: relative;
  transition: color ${HOVER_TRANSITION_DURATION}ms ease;
  padding: ${cssTheme.layout.spacing.s400} 0;
  color: ${cssTheme.colors.tabs.default.content};
  cursor: pointer;

  ${linariaMq.desktopXs} {
    padding: ${cssTheme.layout.spacing.s500} 0;
    &:after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 2px;
      background-color: ${cssTheme.colors.transparent};
      transition: background-color ${HOVER_TRANSITION_DURATION}ms ease;
    }

    &:hover:not(:disabled):not(.active) {
      &:after {
        background-color: ${cssTheme.colors.tabs.hover.border};
      }
    }
  }

  &.fullWidth {
    flex: 1;
    min-width: 0;
  }

  &.scrollable {
    flex: none;
    min-width: fit-content;
  }

  &.active {
    color: ${cssTheme.colors.tabs.active.content};
  }

  &:disabled {
    color: ${cssTheme.colors.tabs.disabled.content};
    cursor: not-allowed;
  }

  &:focus-visible {
    &:before {
      content: '';
      position: absolute;
      left: -${FOCUS_RING_OFFSET}px;
      right: -${FOCUS_RING_OFFSET}px;
      top: ${FOCUS_RING_OFFSET}px;
      bottom: ${FOCUS_RING_OFFSET}px;
      border-radius: ${cssTheme.layout.radius.md};
      box-shadow: inset 0 0 0 2px ${cssTheme.colors.tabs.indicator.background};
      z-index: 1;
    }
  }
`

const TabContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s200};
  min-width: 0;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const LabelWrapper = styled.div`
  white-space: nowrap;

  &.fullWidth {
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const TabComponent = ({
  value,
  label,
  disabled = false,
  className,
  dataId,
  icon,
  variant = 'scrollable'
}: TabProps) => {
  const {activeTab, onTabChange, registerTab, unregisterTab} = useTabsContext()
  const tabRef = useRef<HTMLButtonElement>(null)
  const isActive = activeTab === value

  // Register/unregister tab element for indicator positioning
  useEffect(() => {
    if (tabRef.current) {
      registerTab(value, tabRef.current)
    }

    return () => {
      unregisterTab(value)
    }
  }, [value, registerTab, unregisterTab])

  const handleClick = useCallback(() => {
    if (!disabled) {
      onTabChange(value)
    }
  }, [disabled, onTabChange, value])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        handleClick()
      }
    },
    [handleClick]
  )

  return (
    <TabButton
      ref={tabRef}
      className={cx(className, variant, isActive && 'active')}
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="tab"
      aria-selected={isActive}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      data-id={dataId}
      type="button"
    >
      <TabContent>
        {icon && <IconWrapper>{icon}</IconWrapper>}
        <LabelWrapper className={variant}>
          <Text variant="labelS">{label}</Text>
        </LabelWrapper>
      </TabContent>
    </TabButton>
  )
}

export const Tab = memo(TabComponent)
